import {Helmet, HelmetProvider} from "react-helmet-async";
import {useEffect, useRef, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {Button, Col, Container, Row, Spinner} from "react-bootstrap";

// third party
import Lightbox from "react-18-image-lightbox";
import ReactGA from "react-ga4";
import classNames from "classnames";
import toast from "react-hot-toast";
import moment from "moment/moment";
import parse from "html-react-parser";
import {marked} from "marked";

// images & icons
import {TfiLocationPin} from "react-icons/tfi";
import {AiOutlineClockCircle} from "react-icons/ai";
import {LuEye, LuLayoutDashboard} from "react-icons/lu";
import {LiaClipboardListSolid} from "react-icons/lia";
import {MdArrowBackIos} from "react-icons/md";
import {NoComplex} from "../../assets/images";
// Api
import {getIndividualData, getNearbyVenues} from "../../services/complexservice";
// Redux
import ShowAllImgModal from "../../components/modals/showallimgmodal";
import {IoIosArrowDropleft, IoIosArrowDropright} from "react-icons/io";
import {Swiper, SwiperSlide} from "swiper/react";
import {Autoplay} from "swiper/modules";
// Custom Component
import Layout from "../../components/common/layout";
import Pricing from "../../components/pricing";
import Map from "../../components/map";
import {VenuesByStateCard} from "../../components/common/card";
import ResponsiveView from "./responsiveview";
import {useInView} from "react-intersection-observer";
import _ from "lodash";

const Complex = () => {
    let {slug} = useParams();
    let navigate = useNavigate();
    const swiperFloorPlanRef = useRef(null);
    const swiperVenuesNearbyRef = useRef(null);
    const [swiperFloorPlanIndex, setSwiperFloorPlanIndex] = useState(0);
    const [swiperVenuesNearbyIndex, setSwiperVenuesNearbyIndex] = useState(0);
    const [photoModalShow, setPhotoModalShow] = useState(false);
    const [photoModalIndex, setPhotoModalIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingNearVenue, setLoadingNearVenue] = useState(true);
    const [individualData, setIndividualData] = useState();
    const [nearbyVenue, setNearbyVenue] = useState([]);
    const [imgShowModalShow, setImgShowModalShow] = useState(false);
    const [scrollDepth, setScrollDepth] = useState(0);
    const [lastLoggedDepth, setLastLoggedDepth] = useState(0);

    const {ref, inView} = useInView({
        threshold: 0,
    });

    /**
     * @function handleUpdateModalShow
     * @params (rowData)
     * @description used to show Modal
     */
    const handleAllImgShowModalShow = (rowData) => {
        setImgShowModalShow(!imgShowModalShow);
    };

    /**
     * @function fetchIndividualData
     * @param
     * @description get venue data
     */
    const fetchIndividualData = async () => {
        try {
            setLoading(true);
            const result = await getIndividualData(slug);
            if (result?.data?.data) {
                setIndividualData(result?.data?.data);
                if (result?.data?.data?.id) {
                    await fetchNearByVenues(result?.data?.data?.id);
                }
            } else {
                setIndividualData();
                setNearbyVenue([]);
                toast.error(result?.response?.data?.message || "Something went wrong");
                setLoadingNearVenue(false);
            }
            setLoading(false);
        } catch (error) {
            setIndividualData();
            setNearbyVenue([]);
            setLoading(false);
            setLoadingNearVenue(false);
            toast.error(error || error?.response?.data?.message || "Something went wrong");
        }
    };

    /**
     * @function fetchNearByVenues
     * @param (id)
     * @description get nearby venues List
     */
    const fetchNearByVenues = async (id) => {
        try {
            setLoadingNearVenue(true);
            const result = await getNearbyVenues(id);
            if (result?.data?.data) {
                setNearbyVenue(result?.data?.data);
            } else {
                setNearbyVenue([]);
                toast.error(result?.response?.data?.message || "Something went wrong");
            }
            setLoadingNearVenue(false);
        } catch (error) {
            setNearbyVenue([]);
            setLoadingNearVenue(false);
            toast.error(error || error?.response?.data?.message || "Something went wrong");
        }
    };

    useEffect(() => {
        ReactGA.send({
            hitType: "pageview",
            page: window.location.pathname,
            title: "page_view",
        });
        // eslint-disable-next-line
    }, [slug]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const docHeight = document.documentElement.scrollHeight;
            const winHeight = window.innerHeight;

            // Calculate the scroll percentage
            const scrollPercent = (scrollTop / (docHeight - winHeight)) * 100;
            setScrollDepth(scrollPercent);
        };

        // Add event listener
        window.addEventListener("scroll", handleScroll);

        // Remove event listener on cleanup
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        // Log or perform actions based on scroll depth
        if (scrollDepth >= 100 && lastLoggedDepth < 100) {
            ReactGA.event("scroll_depth", {
                category: "scroll_depth",
                action: "100%",
            });
            setLastLoggedDepth(100);
        } else if (scrollDepth >= 75 && lastLoggedDepth < 75) {
            ReactGA.event("scroll_depth", {
                category: "scroll_depth",
                action: "75%",
            });
            setLastLoggedDepth(75);
        } else if (scrollDepth >= 50 && lastLoggedDepth < 50) {
            ReactGA.event("scroll_depth", {
                category: "scroll_depth",
                action: "50%",
            });
            setLastLoggedDepth(50);
        } else if (scrollDepth >= 25 && lastLoggedDepth < 25) {
            ReactGA.event("scroll_depth", {
                category: "scroll_depth",
                action: "25%",
            });
            setLastLoggedDepth(25);
        }
        // eslint-disable-next-line
    }, [scrollDepth]);

    useEffect(() => {
        fetchIndividualData();
        // eslint-disable-next-line
    }, [slug]);

    return (
        <HelmetProvider>
            <Helmet>
                <title>Complex</title>
            </Helmet>
            <Layout>
                {!loading && !loadingNearVenue ? (
                    <div className="container">
                        {individualData ? (
                            <>
                                <Container className="d-none d-lg-block px-0 border-top">
                                    <Row className="my-3 px- mx-0 ">
                                        <div className="d-flex align-items-center mb-3">
                                            <MdArrowBackIos
                                                role="button"
                                                size={26}
                                                className="me-2"
                                                onClick={() => navigate(-1)}
                                            />
                                            <div className="text-center w-100">
                                                <h4 className="fw-bold mb-0">
                                                    {individualData?.name}
                                                </h4>
                                            </div>
                                        </div>

                                        <div className="my-2 position-relative">
                                            <Row>
                                                <Col>
                                                    <img
                                                        src={individualData?.images[0]}
                                                        alt="SingleComplexImg"
                                                        className="w-100 single-page-banner-height object-fit-scale"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row className="d-none d-md-flex mt-3">
                                                {individualData?.images[1] && (
                                                    <Col md={4}>
                                                        <img
                                                            src={individualData?.images[1]}
                                                            alt="SingleComplexImg"
                                                            className="w-100 single-page-banner-height-30vh"
                                                        />
                                                    </Col>
                                                )}
                                                {individualData?.images[2] && (
                                                    <Col md={4}>
                                                        <img
                                                            src={individualData?.images[2]}
                                                            alt="SingleComplexImg"
                                                            className="w-100 single-page-banner-height-30vh"
                                                        />
                                                    </Col>
                                                )}
                                                {individualData?.images[3] && (
                                                    <Col md={4}>
                                                        <img
                                                            src={individualData?.images[3]}
                                                            alt="SingleComplexImg"
                                                            className="w-100 single-page-banner-height-30vh"
                                                        />
                                                    </Col>
                                                )}
                                            </Row>
                                            <Button
                                                className="text-light position-absolute bottom-0 end-0 mb-3 me-4"
                                                onClick={() => handleAllImgShowModalShow()}
                                            >
                                                <LuEye className="me-2 " />
                                                View All Photos
                                            </Button>
                                        </div>
                                    </Row>
                                    <Row className="my-4 px- mx-0">
                                        <Col md={7}>
                                            <div className="mb-2">
                                                <span className="fs-18 fw-bold">
                                                    <TfiLocationPin className="me-1 mb-1" />
                                                    {individualData?.info?.address}
                                                </span>
                                            </div>
                                            <p className="fs-14">
                                                <span className="fw-bold">Working hours : </span>
                                                {individualData?.opening} -{" "}
                                                {moment(individualData?.opening, "HH:mm")
                                                    .add(individualData?.no_of_hours, "h")
                                                    .format("HH:mm")}
                                            </p>
                                            <hr />

                                            <div className="d-flex align-items-center mb-3 flex-wrap">
                                                {individualData?.facilities?.length > 0 &&
                                                    _.uniqBy(
                                                        _.flatten(
                                                            individualData?.facilities?.map(
                                                                (item) => item?.categories
                                                            )
                                                        )?.filter(
                                                            (item) => !item?.category?.parent_id
                                                        ),
                                                        "category.id"
                                                    )?.length > 0 &&
                                                    _.uniqBy(
                                                        _.flatten(
                                                            individualData?.facilities?.map(
                                                                (item) => item?.categories
                                                            )
                                                        )?.filter(
                                                            (item) => !item?.category?.parent_id
                                                        ),
                                                        "category.id"
                                                    )?.map((item, index) => (
                                                        <div
                                                            className={classNames(
                                                                "d-flex align-items-center me-2 mb-2",
                                                                {
                                                                    "border-end":
                                                                        index !==
                                                                        _.uniqBy(
                                                                            _.flatten(
                                                                                individualData?.facilities?.map(
                                                                                    (item) =>
                                                                                        item?.categories
                                                                                )
                                                                            )?.filter(
                                                                                (item) =>
                                                                                    !item?.category
                                                                                        ?.parent_id
                                                                            ),
                                                                            "category.id"
                                                                        )?.length -
                                                                            1,
                                                                }
                                                            )}
                                                            key={index}
                                                        >
                                                            <img
                                                                className="me-1"
                                                                src={item?.category?.images[0]}
                                                                alt="sportIcon"
                                                                width={16}
                                                            />
                                                            <p className="fs-14 mb-0 me-2">
                                                                {item?.category?.name}
                                                            </p>
                                                        </div>
                                                    ))}
                                            </div>

                                            <hr />
                                            <div className="mb-3">
                                                <span className="fs-18 fw-bold">
                                                    <AiOutlineClockCircle
                                                        size={24}
                                                        className="me-1"
                                                    />
                                                    Opening Hours
                                                </span>
                                            </div>
                                            {individualData?.facilities?.length > 0 &&
                                                individualData?.facilities?.map((item, index) => (
                                                    <p className="fs-12" key={index}>
                                                        {item?.name} daily : {item?.opening} -{" "}
                                                        {moment(item?.opening, "HH:mm")
                                                            .add(item?.no_of_hours, "h")
                                                            .format("HH:mm")}
                                                    </p>
                                                ))}

                                            <hr />

                                            <div className="mb-3">
                                                <span className="fs-18 fw-bold">
                                                    <AiOutlineClockCircle
                                                        size={24}
                                                        className="me-1"
                                                    />
                                                    Amenities & Facilities
                                                </span>
                                            </div>
                                            <Row>
                                                <Col>
                                                    <p className="fw-bold">Amenities</p>
                                                    {individualData?.amenities?.length > 0 &&
                                                        individualData?.amenities?.map(
                                                            (item, index) => (
                                                                <div
                                                                    className="d-flex align-items-center mb-3"
                                                                    key={`amenities${index}`}
                                                                >
                                                                    <img
                                                                        className="rounded-circle bg-dar me-2"
                                                                        src={
                                                                            item?.amenity?.images[0]
                                                                        }
                                                                        width={20}
                                                                        alt="amenity pic"
                                                                    />
                                                                    <span className="fs-14">
                                                                        {item?.amenity?.name}
                                                                    </span>
                                                                </div>
                                                            )
                                                        )}
                                                </Col>
                                                <Col>
                                                    <p className="fw-bold">Facilities</p>
                                                    {individualData?.facilities?.length > 0 &&
                                                        individualData?.facilities?.map(
                                                            (item, index) => (
                                                                <p className="fs-14" key={index}>
                                                                    {item?.name}
                                                                </p>
                                                            )
                                                        )}
                                                </Col>
                                            </Row>
                                            <hr />

                                            <div className="d-flex flex-column align-items-start mb-3">
                                                <span className="fs-18 fw-bold">
                                                    <LuLayoutDashboard size={24} className="me-1" />
                                                    Centre Layout
                                                </span>
                                                {individualData?.info?.floor_plan?.length > 1 ? (
                                                    <div className="my-3 d-flex align-items-center w-100">
                                                        <div className="mb-3">
                                                            <IoIosArrowDropleft
                                                                role={
                                                                    swiperFloorPlanIndex > 0
                                                                        ? "button"
                                                                        : ""
                                                                }
                                                                className={classNames("me-2", {
                                                                    "text-primary":
                                                                        swiperFloorPlanIndex > 0,
                                                                })}
                                                                color={
                                                                    swiperFloorPlanIndex === 0
                                                                        ? "grey"
                                                                        : ""
                                                                }
                                                                size={24}
                                                                onClick={() =>
                                                                    swiperFloorPlanRef.current?.slidePrev()
                                                                }
                                                            />
                                                        </div>
                                                        <Swiper
                                                            className="floor-plan-swiper-slider"
                                                            slidesPerView={"auto"}
                                                            spaceBetween={0}
                                                            modules={[Autoplay]}
                                                            autoplay={{
                                                                delay: 3000,
                                                                disableOnInteraction: false,
                                                            }}
                                                            onBeforeInit={(swiper) => {
                                                                swiperFloorPlanRef.current = swiper;
                                                            }}
                                                            onReachEnd={(swiper) => {
                                                                setSwiperFloorPlanIndex(
                                                                    individualData?.info?.floor_plan
                                                                        ?.length
                                                                );
                                                            }}
                                                            onSlideChange={(swiper) => {
                                                                if (swiper.isEnd) {
                                                                    setSwiperFloorPlanIndex(
                                                                        individualData?.info
                                                                            ?.floor_plan?.length
                                                                    );
                                                                } else {
                                                                    setSwiperFloorPlanIndex(
                                                                        swiper?.activeIndex
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            {individualData?.info?.floor_plan?.map(
                                                                (item, index) => {
                                                                    return (
                                                                        <SwiperSlide
                                                                            key={`floor-plan-${index}`}
                                                                        >
                                                                            <div
                                                                                className={classNames(
                                                                                    "rounded-4 h-100"
                                                                                )}
                                                                                role="button"
                                                                            >
                                                                                <img
                                                                                    className="img-fluid w-100"
                                                                                    src={item}
                                                                                    alt="floor_plan"
                                                                                    role="button"
                                                                                    onClick={() => {
                                                                                        setPhotoModalShow(
                                                                                            true
                                                                                        );
                                                                                        setPhotoModalIndex(
                                                                                            index
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </SwiperSlide>
                                                                    );
                                                                }
                                                            )}
                                                        </Swiper>
                                                        <div className="ms-3 mb-3">
                                                            <IoIosArrowDropright
                                                                role={
                                                                    swiperFloorPlanIndex <
                                                                    individualData?.info?.floor_plan
                                                                        ?.length
                                                                        ? "button"
                                                                        : ""
                                                                }
                                                                className={classNames({
                                                                    "text-primary":
                                                                        swiperFloorPlanIndex <
                                                                        individualData?.info
                                                                            ?.floor_plan?.length,
                                                                })}
                                                                color={
                                                                    swiperFloorPlanIndex ===
                                                                    individualData?.info?.floor_plan
                                                                        ?.length
                                                                        ? "grey"
                                                                        : ""
                                                                }
                                                                size={24}
                                                                onClick={() =>
                                                                    swiperFloorPlanRef.current?.slideNext()
                                                                }
                                                            />
                                                        </div>

                                                        {photoModalShow && (
                                                            <Lightbox
                                                                mainSrc={
                                                                    individualData?.info
                                                                        ?.floor_plan[
                                                                        photoModalIndex
                                                                    ]
                                                                }
                                                                nextSrc={
                                                                    individualData?.info
                                                                        ?.floor_plan[
                                                                        (photoModalIndex + 1) %
                                                                            individualData?.info
                                                                                ?.floor_plan.length
                                                                    ]
                                                                }
                                                                prevSrc={
                                                                    individualData?.info
                                                                        ?.floor_plan[
                                                                        (photoModalIndex +
                                                                            individualData?.info
                                                                                ?.floor_plan
                                                                                .length -
                                                                            1) %
                                                                            individualData?.info
                                                                                ?.floor_plan.length
                                                                    ]
                                                                }
                                                                onMovePrevRequest={() =>
                                                                    setPhotoModalIndex(
                                                                        (photoModalIndex +
                                                                            individualData?.info
                                                                                ?.floor_plan
                                                                                .length -
                                                                            1) %
                                                                            individualData?.info
                                                                                ?.floor_plan.length
                                                                    )
                                                                }
                                                                onMoveNextRequest={() =>
                                                                    setPhotoModalIndex(
                                                                        (photoModalIndex + 1) %
                                                                            individualData?.info
                                                                                ?.floor_plan.length
                                                                    )
                                                                }
                                                                onCloseRequest={() => {
                                                                    setPhotoModalShow(false);
                                                                    setPhotoModalIndex(0);
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                ) : (
                                                    <>
                                                        <img
                                                            width="50%"
                                                            className="mt-3"
                                                            src={
                                                                individualData?.info?.floor_plan
                                                                    ?.length > 0
                                                                    ? individualData?.info
                                                                          ?.floor_plan[0]
                                                                    : ""
                                                            }
                                                            alt="floor_plan"
                                                            role="button"
                                                            onClick={() => setPhotoModalShow(true)}
                                                        />
                                                        {photoModalShow && (
                                                            <Lightbox
                                                                mainSrc={
                                                                    individualData?.info
                                                                        ?.floor_plan[0]
                                                                }
                                                                onCloseRequest={() =>
                                                                    setPhotoModalShow(false)
                                                                }
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <hr />

                                            <div className="mb-3">
                                                <span className="fs-18 fw-bold">
                                                    <LiaClipboardListSolid
                                                        size={24}
                                                        className="me-1"
                                                    />
                                                    Centre Policy
                                                </span>
                                                <div className="center-policy mt-3">
                                                    {individualData?.info?.center_policy
                                                        ? parse(
                                                              marked(
                                                                  individualData?.info
                                                                      ?.center_policy
                                                              )
                                                          )
                                                        : ""}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={5} className="mt-3 mt-md-0">
                                            <Pricing individualData={individualData} />
                                        </Col>
                                    </Row>
                                    <Row className="my-3 px- mx-0 ">
                                        <hr />
                                        <p className="fs-18 fw-bold">Location of Complex</p>
                                        <span className="fs-16">
                                            <TfiLocationPin className="me-1 mb-1" />
                                            {individualData?.info?.address}
                                        </span>
                                        {individualData?.location_lat &&
                                            individualData?.location_long && (
                                                <div className="mt-3 mb-3">
                                                    <Map
                                                        lat={individualData?.location_lat}
                                                        lng={individualData?.location_long}
                                                    />
                                                </div>
                                            )}
                                    </Row>

                                    <Row ref={ref} className="mt-1 mb-5 px- mx-0 ">
                                        <div
                                            className={classNames("", {
                                                "d-none": !inView,
                                                "z-2": inView,
                                            })}
                                        >
                                            <Link
                                                to={`${process.env.REACT_APP_BOOKING_URL}/?auth=${individualData?.id}`}
                                                onClick={() => {
                                                    ReactGA.event("contact_initiated", {
                                                        category: "contact_initiated",
                                                        action: JSON.stringify({
                                                            id: individualData?.id,
                                                            name: individualData?.name,
                                                        }),
                                                    });
                                                }}
                                            >
                                                <Button
                                                    className={"text-light mb-3 w-100"}
                                                    variant={"primary"}
                                                >
                                                    Book Venue
                                                </Button>
                                            </Link>
                                        </div>

                                        <div
                                            className={classNames("px-5 text-center", {
                                                "z-2 fixed-bottom": !inView,
                                                "d-none": inView,
                                            })}
                                        >
                                            <Link
                                                to={`${process.env.REACT_APP_BOOKING_URL}/?auth=${individualData?.id}`}
                                                onClick={() => {
                                                    ReactGA.event("contact_initiated", {
                                                        category: "contact_initiated",
                                                        action: JSON.stringify({
                                                            id: individualData?.id,
                                                            name: individualData?.name,
                                                        }),
                                                    });
                                                }}
                                            >
                                                <Button
                                                    className={"text-light w-100"}
                                                    variant={"primary"}
                                                >
                                                    Book Venue
                                                </Button>
                                            </Link>
                                        </div>

                                        <h4 className="fw-bold">Venues Nearby</h4>
                                        <div className="my-3 d-flex align-items-center">
                                            <div className="mb-3">
                                                <IoIosArrowDropleft
                                                    role={
                                                        swiperVenuesNearbyIndex > 0 ? "button" : ""
                                                    }
                                                    className={classNames("me-2", {
                                                        "text-primary": swiperVenuesNearbyIndex > 0,
                                                    })}
                                                    color={
                                                        swiperVenuesNearbyIndex === 0 ? "grey" : ""
                                                    }
                                                    size={24}
                                                    onClick={() =>
                                                        swiperVenuesNearbyRef.current?.slidePrev()
                                                    }
                                                />
                                            </div>
                                            <Swiper
                                                className="venue-swiper-slider"
                                                slidesPerView={"auto"}
                                                spaceBetween={0}
                                                onBeforeInit={(swiper) => {
                                                    swiperVenuesNearbyRef.current = swiper;
                                                }}
                                                onReachEnd={(swiper) => {
                                                    setSwiperVenuesNearbyIndex(nearbyVenue?.length);
                                                }}
                                                onSlideChange={(swiper) => {
                                                    if (swiper.isEnd) {
                                                        setSwiperVenuesNearbyIndex(
                                                            nearbyVenue?.length
                                                        );
                                                    } else {
                                                        setSwiperVenuesNearbyIndex(
                                                            swiper?.activeIndex
                                                        );
                                                    }
                                                }}
                                            >
                                                {nearbyVenue?.length > 0 &&
                                                    nearbyVenue?.map((item, index) => {
                                                        return (
                                                            <SwiperSlide key={`venue${index}`}>
                                                                <div
                                                                    key={index}
                                                                    className={classNames(
                                                                        "me- rounded-4 px-3 py-1 h-100"
                                                                    )}
                                                                    role="button"
                                                                    // onClick={() => stateSelection(item)}
                                                                >
                                                                    <VenuesByStateCard
                                                                        item={item}
                                                                        nearBy={true}
                                                                    />
                                                                </div>
                                                            </SwiperSlide>
                                                        );
                                                    })}
                                            </Swiper>
                                            <div className="ms-3 mb-3">
                                                <IoIosArrowDropright
                                                    role={
                                                        swiperVenuesNearbyIndex <
                                                        nearbyVenue?.length
                                                            ? "button"
                                                            : ""
                                                    }
                                                    className={classNames({
                                                        "text-primary":
                                                            swiperVenuesNearbyIndex <
                                                            nearbyVenue?.length,
                                                    })}
                                                    color={
                                                        swiperVenuesNearbyIndex ===
                                                        nearbyVenue?.length
                                                            ? "grey"
                                                            : ""
                                                    }
                                                    size={24}
                                                    onClick={() =>
                                                        swiperVenuesNearbyRef.current?.slideNext()
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </Row>
                                </Container>

                                <Container className="d-block d-lg-none border-top">
                                    <ResponsiveView
                                        individualData={individualData}
                                        nearbyVenue={nearbyVenue}
                                    />
                                </Container>
                            </>
                        ) : (
                            <>
                                <div className="d-flex flex-column justify-content-center align-items-center py-5">
                                    <img src={NoComplex} alt="NoComplex" className="img-fluid" />
                                    <span className="d-none d-sm-block fs-30 fw-600 text-center">
                                        No Complex Found
                                    </span>
                                    <span className="d-sm-none fs-18 fw-600 text-center">
                                        No Complex Found
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <Container className="text-center py-5">
                        <Spinner />
                    </Container>
                )}
                <ShowAllImgModal
                    show={imgShowModalShow}
                    handleClose={handleAllImgShowModalShow}
                    individualData={individualData}
                />
            </Layout>
        </HelmetProvider>
    );
};

export default Complex;
